import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabaseClient } from '../../config/supabase';
import {
    DAYS, END_OF_DAY, dayRowProblem, normalizeTime, timeZoneOptions,
} from '../../lib/businessHours';
import {
    cleanHolidays, describePause, fetchAiIntegration, isRealIsoDate, loadBusinessSchedule, pauseAllAi, resumeAllAi,
    saveBusinessDay, saveBusinessHolidays, saveBusinessZone,
} from '../../lib/businessSchedule';
import { useSaveToast } from '../ui/SaveToast';
import Switch from '../ui/Switch';
import Button3D from '../ui/Button3D';
import HelpButton from '../ui/HelpButton';
import { TimeField } from '../ui/TimeField';
import AiAssistantCompartment from '../ai/AiAssistantCompartment';
import { D2_INPUT, D2_SECTION_TITLE } from '../settings/signatures/d2Fields';
import InboundCallSettingsPanel from './InboundCallSettingsPanel';

// BL-177 Step 2m: Admin Center › Business schedule.
//   1. The company's weekly OPEN HOURS (one source of truth: the imported provider hours are now a read-only copy of these), the company
//      time zone, and holidays (closed all day).
//   2. "Pause all AI": stops every AI reply right now, shows who paused and when, and Resume.
//   3. The AI integration compartment (Off / On / Pause / Test, then the assistant's options).
// Every control saves on its own: green "Saved ✓", or a red toast and the control switches back.
const FAILED_PREFIX = "Couldn't save. Switched back.";

export default function BusinessSchedulePage({ orgId, client = supabaseClient }) {
    const toast = useSaveToast();
    const location = useLocation();
    const [schedule, setSchedule] = useState(null);
    const [hours, setHours] = useState({});
    const [error, setError] = useState('');
    const [pause, setPause] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [holiday, setHoliday] = useState('');
    const saved = useRef({});
    const compartmentAnchor = useRef(null);

    const load = useCallback(async () => {
        try {
            const next = await loadBusinessSchedule(client, orgId);
            setSchedule(next);
            setHours(next.hours);
            saved.current = next.hours;
            setError('');
        } catch (loadError) {
            setError(loadError?.message || 'The business schedule could not be loaded.');
        }
    }, [client, orgId]);
    useEffect(() => { load(); }, [load]);

    const loadPause = useCallback(async () => {
        try { setPause(await fetchAiIntegration(client, orgId)); } catch { setPause(null); }
    }, [client, orgId]);
    useEffect(() => { loadPause(); }, [loadPause, reloadKey]);

    // Deep link from the Communications "AI:" pill: /admin/business-schedule#ai-integration
    useEffect(() => {
        if (location.hash === '#ai-integration' && schedule) compartmentAnchor.current?.scrollIntoView?.({ block: 'start' });
    }, [location.hash, schedule]);

    async function persist(apply, revert, write) {
        apply();
        try { await write(); toast.saved(); } catch (saveError) { revert(); toast.failed(`${FAILED_PREFIX} ${saveError?.message || 'Try again.'}`); }
    }

    function saveDay(dow, nextDay) {
        const previous = saved.current[dow];
        return persist(
            () => setHours((current) => ({ ...current, [dow]: nextDay })),
            () => setHours((current) => ({ ...current, [dow]: previous })),
            async () => { await saveBusinessDay(client, orgId, dow, nextDay); saved.current = { ...saved.current, [dow]: nextDay }; },
        );
    }
    function toggleDay(dow, open) {
        const day = hours[dow] || {};
        saveDay(dow, { is_open: open, opens_at: day.opens_at || '06:00', closes_at: day.closes_at || END_OF_DAY });
    }
    function commitTime(dow) {
        const day = hours[dow];
        const was = saved.current[dow] || {};
        if (!day || (day.opens_at === was.opens_at && day.closes_at === was.closes_at)) return;
        const problem = dayRowProblem(day);
        if (problem) { setHours((current) => ({ ...current, [dow]: was })); toast.failed(`${FAILED_PREFIX} ${problem}`); return; }
        saveDay(dow, day);
    }
    function saveZone(timeZone) {
        const before = schedule;
        return persist(
            () => setSchedule((current) => ({ ...current, time_zone: timeZone })),
            () => setSchedule(before),
            () => saveBusinessZone(client, orgId, timeZone),
        );
    }
    function saveHolidays(next) {
        const before = schedule;
        const holidays = cleanHolidays(next);
        return persist(
            () => setSchedule((current) => ({ ...current, holidays })),
            () => setSchedule(before),
            () => saveBusinessHolidays(client, orgId, holidays),
        );
    }
    function addHoliday() {
        if (!isRealIsoDate(holiday)) { toast.failed(`${FAILED_PREFIX} Pick a real date.`); return; }
        saveHolidays([...(schedule.holidays || []), holiday]);
        setHoliday('');
    }
    async function togglePauseAll() {
        const paused = Boolean(describePause(pause));
        try {
            if (paused) await resumeAllAi(client, orgId); else await pauseAllAi(client, orgId);
            toast.saved();
            setReloadKey((key) => key + 1);
        } catch (failure) {
            toast.failed(`${FAILED_PREFIX} ${failure?.message || ''}`.trim());
        }
    }

    if (error) return <div role="alert" className="rounded-lg border border-red-500/50 bg-red-500/10 p-4 text-sm text-[var(--ink)]">{error}</div>;
    if (!schedule) return <div className="p-6 text-[var(--ink-muted)]">Loading…</div>;

    const pausedLine = describePause(pause);
    return (
        <div className="space-y-6" data-testid="business-schedule">
            <section className="ia-d2 ia-d2-card space-y-3 rounded-xl p-4" aria-labelledby="bs-hours-title">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <h3 id="bs-hours-title" className={D2_SECTION_TITLE}>
                        Open hours
                        <HelpButton
                            title="Open hours"
                            what="The days and hours your company is open. This is the one place to set them: Incoming messages' after-hours tag and the AI assistant's after-hours cover both follow it."
                            who="Company admins and platform admins."
                            when="Checked the moment each client message arrives, on the clock in the time zone below (daylight saving included)."
                        />
                    </h3>
                    <div className="flex flex-col items-end gap-1" data-testid="pause-all">
                        <Button3D variant={pausedLine ? 'go' : 'primary'} onClick={togglePauseAll}>{pausedLine ? 'Resume AI' : 'Pause all AI'}</Button3D>
                        {pausedLine && <span role="status" className="max-w-xs text-right text-xs font-bold text-white">{pausedLine}</span>}
                        {/* BL-177 Step 2o: "Resume" here only undoes "Pause all AI". A thread a person took over has its own pause and its own Resume AI. */}
                        <span data-testid="pause-all-scope" className="max-w-xs text-right text-[11px] leading-snug text-gray-300">
                            {pausedLine ? 'Resume clears only this company-wide pause.' : 'Pause all AI is company-wide.'} A thread a person took over stays paused until you press Resume AI in that thread.
                        </span>
                    </div>
                </div>

                <label className="flex flex-wrap items-center gap-3 text-sm font-bold text-white">
                    Time zone
                    <select value={schedule.time_zone} onChange={(event) => saveZone(event.target.value)} className={D2_INPUT}>
                        {timeZoneOptions(schedule.time_zone).map((zone) => <option key={zone.value} value={zone.value}>{zone.label}</option>)}
                    </select>
                </label>

                <div role="table" aria-label="Open hours by day" className="max-w-full text-white">
                    <div role="row" className="hidden grid-cols-[minmax(4rem,4.5rem)_minmax(5.5rem,5.75rem)_minmax(4rem,1fr)_auto_minmax(4rem,1fr)] items-center gap-x-1 border-b border-[#1E2A42] pb-1 text-xs font-bold uppercase tracking-wider sm:grid">
                        <span role="columnheader">Day</span>
                        <span role="columnheader">Open</span>
                        <span role="columnheader">Opens</span>
                        <span aria-hidden="true" />
                        <span role="columnheader">Closes</span>
                    </div>
                    {DAYS.map(({ dow, name }) => {
                        const day = hours[dow] || { is_open: false, opens_at: '', closes_at: '' };
                        const overnight = day.is_open && day.closes_at && day.opens_at && day.closes_at < day.opens_at;
                        return (
                            <div role="row" key={dow} data-day={dow} className="grid grid-cols-[minmax(4rem,4.5rem)_minmax(5.5rem,5.75rem)_minmax(4rem,1fr)_auto_minmax(4rem,1fr)] items-center gap-x-1 border-t border-[#1E2A42] py-2 text-sm first-of-type:border-t-0">
                                <span role="rowheader" className="font-bold">{name}</span>
                                <span role="cell" className="inline-flex min-w-0 items-center gap-1">
                                    <Switch label={`${name} open`} showState={false} checked={day.is_open === true} onChange={(open) => toggleDay(dow, open)} />
                                    <span className="truncate">{day.is_open ? 'Open' : 'Closed'}</span>
                                </span>
                                {day.is_open ? (
                                    <>
                                        <span role="cell" className="min-w-0">
                                            <TimeField label={`${name} opens`} value={day.opens_at || ''}
                                                className="h-8 w-full min-w-0 px-1 py-0 text-xs sm:max-w-[8.5rem]"
                                            onChange={(value) => setHours({ ...hours, [dow]: { ...day, opens_at: normalizeTime(value) } })} onBlur={() => commitTime(dow)} />
                                        </span>
                                        <span role="cell" className="text-center text-xs">to</span>
                                        <span role="cell" className="min-w-0">
                                            <TimeField label={`${name} closes`} value={day.closes_at || ''}
                                                className="h-8 w-full min-w-0 px-1 py-0 text-xs sm:max-w-[8.5rem]"
                                            onChange={(value) => setHours({ ...hours, [dow]: { ...day, closes_at: normalizeTime(value) } })} onBlur={() => commitTime(dow)} />
                                        </span>
                                        {overnight && <span role="cell" className="col-span-5 text-xs">(next day)</span>}
                                    </>
                                ) : (
                                    <span role="cell" className="col-span-3 text-xs text-[#C7D2E8]" aria-label={`${name} closed all day`}>
                                        Closed all day
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="space-y-2 border-t border-[#1E2A42] pt-3 text-white">
                    <div className="text-sm font-bold">Holidays <span className="font-normal text-[#C7D2E8]">(closed all day)</span></div>
                    <div className="flex flex-wrap items-center gap-2">
                        <input type="date" aria-label="Holiday date" value={holiday} onChange={(event) => setHoliday(event.target.value)} className={D2_INPUT} />
                        <Button3D variant="light" onClick={addHoliday} disabled={!holiday}>Add holiday</Button3D>
                    </div>
                    {schedule.holidays.length > 0 && (
                        <ul className="flex flex-wrap gap-2" aria-label="Holidays">
                            {schedule.holidays.map((date) => (
                                <li key={date} className="inline-flex items-center gap-2 rounded-full border border-[#33415E] bg-[#0A101C] px-3 py-1 text-xs font-bold">
                                    {date}
                                    <button type="button" aria-label={`Remove ${date}`} className="text-[#5EEAD4]" onClick={() => saveHolidays(schedule.holidays.filter((other) => other !== date))}>×</button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            <InboundCallSettingsPanel orgId={orgId} client={client} />

            <div ref={compartmentAnchor}>
                <AiAssistantCompartment orgId={orgId} client={client} hoursByDay={hours} reloadKey={reloadKey} onChanged={loadPause} />
            </div>
        </div>
    );
}
